import { useMemo } from "react"
import { GetApp } from "../App"
import { GetShipContext } from "../App/Context"
import { Icon, Icons } from "./Icon"
import { Loading } from "./Loading"
import { Menu, MenuItemDividerProps } from "./Menu"
import { ShipUserSelectData } from "./ShipUserSelect"
import { AccountApi } from "../Types"
import { AppRoutes, GetAppView } from "../Views/AppView"
import { AppOverlayContent } from "../Views/AppOverlayContentView"
import { ShipRoutes } from "../Views/ShipView"

export interface MenuAccountProps {
  // Opt-in initial open state, forwarded to the underlying `Menu`. Real callers
  // never set this (menus always start closed); it exists so catalog fixtures
  // can preview the item list itself, not just the collapsed button (#384).
  initOpen?: boolean;
}

export function MenuAccount({ initOpen }: MenuAccountProps) {
  const app = GetApp();
  const { navigateApp, setAppOverlay, actingShipUser, setActingShipUser } = GetAppView();
  // The real, authenticated ShipUser for this ship (never the acting override) -- used to
  // name the "return to <name>" item below (#197/#1026 (tracker #827) user switch).
  const { shipUser, apiUser } = GetShipContext();
  const t = app.getT("MenuAccount");
  const shipId = app.shipId;
  // account.delete() is the same generated permission-matrix predicate that gates the
  // "Remove account" button inside Account Settings > Your Subscription (AccountView.tsx) --
  // surfacing a shortcut directly in the top-level menu makes account deletion reasonably
  // discoverable without requiring users to find that tab first (App Store review
  // guideline 5.1.1(v)).
  const accountDeletable = useMemo(() => new AccountApi(app.apiClient).account.delete({ params: {} }).available(apiUser), [app.apiClient, apiUser]);

  return (
    <Menu button={app.user ? <><Icon icon={Icons.User} /> {app.user.shortname || app.user.name}</> : <Loading />} global={true} initOpen={initOpen} items={[
      // Account Settings is an account-level feature -- hidden while attributing new
      // entries to another crew member (#197/#1026 user switch).
      ...(actingShipUser ? [] : [{ icon: Icons.Account, id: "account", onClick: () => {
        navigateApp(AppRoutes.accountSettings);
        return true;
      }, title: t("account_settings") }]),

      // Same visibility rule as the "account" item above (account-level, hidden while
      // acting as another crew member), plus cloudSession()/accountDeletable gating
      // mirroring AccountSettingsView.tsx's accountDeletable check. Opens the same
      // removeAccount confirmation overlay used by AccountSettingsAccountView.
      ...(actingShipUser || !app.cloudSession() || !accountDeletable ? [] : [{ icon: Icons.Trash, id: "removeAccount", onClick: () => {
        setAppOverlay(AppOverlayContent.removeAccount);
        return true;
      }, title: t("remove_account") }]),

      { icon: Icons.User, id: "userSettings", onClick: () => {
        setAppOverlay(AppOverlayContent.userSettings);
        return true;
      }, title: t("user_settings") },

      ...(!app.hasFeature("multi-user") || !shipId ? [] : [MenuItemDividerProps,
        { id: "userSwitch", onClick: () => {
          navigateApp(AppRoutes.ship, { shipId, shipRoute: ShipRoutes.userSwitch });
          return true;
        }, title: t("switch_user") }]),

      // switcher between cloud and app
      ...(!app.cloudSession() ? [MenuItemDividerProps,
        { icon: Icons.Cloud, id: "openCloud", onClick: () => {
          // openServer resolves the real server host and applies the iOS
          // inapp=true opt-in itself (see App.tsx) -- calling it directly avoids
          // hardcoding logbook.yachts and duplicating that platform logic here.
          app.openServer("");
          return true;
        }, title: t("open_cloud") }] : [MenuItemDividerProps,
        { id: "openAppAccount", onClick: () => {
          // window.open(..., "_blank") for this unregistered custom scheme renders a
          // blank page inside SFSafariViewController on iPad instead of prompting
          // "Open in App?" (#384) -- location.href is the pattern that actually
          // works there, matching EmptyView.tsx's app-launch link.
          window.location.href = "logbook://app/account";
          return true;
        }, title: t("account_settings_app") }]),

      MenuItemDividerProps,
      // While acting as another ShipUser, the bottom item always jumps back to the real
      // identity/session first -- this is the explicit "jump back to main session" action,
      // it never automatically clears (#197/#1026 user switch). Names the real session's
      // ShipUser rather than a generic "My Account" label, since Account already has a
      // distinct meaning elsewhere in this app.
      actingShipUser ? { id: "returnToOwnUser", onClick: () => {
        setActingShipUser(undefined);
        return true;
      }, title: t("return_to_own_user", { name: ShipUserSelectData.userLabel(shipUser || {}) }) } : { id: "logout", onClick: async () => {
        if (app.primarySession() && app.shipId) {
          setAppOverlay(AppOverlayContent.logout);
        } else {
          await app.logout();
        }
        return true;
      }, title: app.localSession() ? t("close_companion") : t("logout") },
    ]}
    />
  );
}
